import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import '../styles/driver-udvega.css';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let activeDriver = null;
let completing = false;

async function markTourComplete(onComplete) {
  if (completing) return;
  completing = true;
  try {
    await axios.post(`${API}/auth/complete-tour`, {}, { withCredentials: true });
  } catch {
    try {
      await axios.post(`${API}/complete-tour/`, {}, { withCredentials: true });
    } catch {
      /* ignore network errors — tour still ends locally */
    }
  } finally {
    if (typeof onComplete === 'function') onComplete();
    completing = false;
  }
}

function labelSkipButton() {
  const closeBtn = document.querySelector('.udvega-tour-popover .driver-popover-close-btn');
  if (closeBtn) {
    closeBtn.setAttribute('aria-label', 'Skip Tour');
    closeBtn.title = 'Skip Tour';
    closeBtn.textContent = 'Skip Tour';
  }
}

/**
 * Start the first-time HR dashboard product tour (Driver.js).
 * Call only when has_seen_onboarding_tour is false on the main dashboard.
 */
export function startDashboardOnboardingTour({ onComplete } = {}) {
  if (typeof document === 'undefined') return null;

  if (activeDriver) {
    try {
      activeDriver.destroy();
    } catch {
      /* ignore */
    }
    activeDriver = null;
  }

  const steps = [
    {
      element: '[data-tour="tour-create-survey"]',
      popover: {
        title: 'Welcome to Udvega! 👋',
        description: "Let's launch your first anonymous employee pulse in under 60 seconds.",
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="tour-team-select"]',
      popover: {
        title: 'Select Your Target Team',
        description:
          'Choose which department to evaluate — Engineering & Tech, Sales & Business Development, or add a custom team.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '[data-tour="tour-anonymity-settings"]',
      popover: {
        title: 'Strict Anonymity Guaranteed',
        description: 'Confirm survey settings. Remember, no email IDs or PII are ever collected.',
        side: 'top',
        align: 'start',
      },
    },
    {
      element: '[data-tour="tour-launch-cta"]',
      popover: {
        title: 'Launch Your First Pulse 🚀',
        description: 'Click here to send out automated anonymous pulse links to your team!',
        side: 'top',
        align: 'start',
      },
    },
  ].filter((step) => document.querySelector(step.element));

  if (steps.length === 0) return null;

  let finished = false;
  const finish = () => {
    if (finished) return;
    finished = true;
    activeDriver = null;
    markTourComplete(onComplete);
  };

  // Avoid onDestroyStarted entirely — in driver.js 1.3 it blocks teardown and
  // does not pass a driver instance on the hook opts (opts.driver is undefined).
  const tour = driver({
    showProgress: true,
    animate: true,
    allowClose: true,
    overlayColor: 'rgba(2, 6, 23, 0.72)',
    stagePadding: 10,
    stageRadius: 12,
    popoverClass: 'udvega-tour-popover',
    showButtons: ['next', 'previous', 'close'],
    nextBtnText: 'Next',
    prevBtnText: 'Back',
    doneBtnText: 'Done',
    progressText: '{{current}} / {{total}}',
    steps,
    onPopoverRender: () => {
      labelSkipButton();
    },
    onNextClick: () => {
      if (tour.isLastStep()) {
        tour.destroy();
      } else {
        tour.moveNext();
      }
    },
    onPrevClick: () => {
      tour.movePrevious();
    },
    onCloseClick: () => {
      tour.destroy();
    },
    onDestroyed: () => {
      finish();
    },
  });

  activeDriver = tour;
  tour.drive();
  return tour;
}
